import {
  buildDraftOccurrencesFromSnapshot,
  buildDraftOccurrencesFromTask,
  buildScheduleClassOptions,
  buildScheduleTeacherOptions,
  buildSubjectWeeklyFrequencyMap,
  createOccurrenceKey,
  isSubgroupVisibleForTarget,
  scheduleApi,
  sortTeacherOptionsBySubjectAndName
} from '@/api/schedule'
import { useScheduleConflicts } from '@/composables/useScheduleConflicts'
import { applyLinkedSwapPlan } from '@/composables/scheduleLinkedSwap'
import { useScheduleStore } from '@/stores/schedule'
import { logger } from '@/utils/logger'
import type {
  ConflictInfo,
  ScheduleOccurrenceDraft,
  ScheduleSlot,
  ScheduleSnapshotDetail,
  ScheduleClassOption,
  ScheduleTaskDetail,
  StagedCourse,
  TeacherSchedule,
  ScheduleTeacherOption
} from '@/types'

type UseScheduleDraftReturn = {
  buildStagedCourses: (occurrences: ScheduleOccurrenceDraft[]) => StagedCourse[]
  buildClassSchedule: (
    occurrences: ScheduleOccurrenceDraft[],
    classId: number,
    subgroupId?: number | null
  ) => ScheduleSlot[]
  buildTeacherScheduleFromDrafts: (
    occurrences: ScheduleOccurrenceDraft[],
    teacher: ScheduleTeacherOption | null
  ) => TeacherSchedule | null
  mergeTeacherOptions: (
    task: ScheduleTaskDetail | null,
    snapshot: ScheduleSnapshotDetail | null,
    draftOccurrences: ScheduleOccurrenceDraft[]
  ) => ScheduleTeacherOption[]
  overlayDraftOccurrencesFromResults: (
    baseOccurrences: ScheduleOccurrenceDraft[],
    schedulesByClass: Record<number, ScheduleSlot[]>
  ) => ScheduleOccurrenceDraft[]
  findOccurrence: (key: string) => ScheduleOccurrenceDraft | null
  moveOccurrenceToSlot: (key: string, dayOfWeek: number, period: number, force?: boolean) => ConflictInfo
  moveOccurrenceToStaging: (key: string) => boolean
  discardChanges: () => void
  applyDraftOccurrences: (
    occurrences: ScheduleOccurrenceDraft[],
    snapshot: ScheduleSnapshotDetail | null
  ) => void
  markBaselineFromSave: (
    occurrences: ScheduleOccurrenceDraft[],
    snapshot: ScheduleSnapshotDetail | null
  ) => void
  serializeDraftOccurrences: (occurrences: ScheduleOccurrenceDraft[]) => string
  hydrateDraftFromResults: (task: ScheduleTaskDetail) => Promise<void>
  loadActiveSnapshot: (snapshotId: number) => Promise<void>
}

/**
 * 草稿编排逻辑 composable
 *
 * 职责：
 * - 草稿课时的克隆、排序、序列化
 * - 草稿到各种数据结构的转换（slot, stagedCourse, classSchedule, teacherSchedule）
 * - 草稿的查找、移动、丢弃等操作
 * - 教师选项的合并
 */
export function useScheduleDraft(): UseScheduleDraftReturn {
  const conflictsComposable = useScheduleConflicts()

  // ==================== 工具函数 ====================

  function cloneDraftOccurrence(occurrence: ScheduleOccurrenceDraft): ScheduleOccurrenceDraft {
    return {
      ...occurrence
    }
  }

  function cloneDraftOccurrences(occurrences: ScheduleOccurrenceDraft[]): ScheduleOccurrenceDraft[] {
    return occurrences.map((occurrence) => cloneDraftOccurrence(occurrence))
  }

  function sortDraftOccurrences(occurrences: ScheduleOccurrenceDraft[]): ScheduleOccurrenceDraft[] {
    return [...occurrences].sort((left, right) => {
      if (left.taskActivityId !== right.taskActivityId) {
        return left.taskActivityId - right.taskActivityId
      }
      return left.occurrenceIndex - right.occurrenceIndex
    })
  }

  function serializeDraftOccurrences(occurrences: ScheduleOccurrenceDraft[]): string {
    return JSON.stringify(
      sortDraftOccurrences(occurrences).map((occurrence) => ({
        key: occurrence.key,
        placed: occurrence.placed,
        dayOfWeek: occurrence.dayOfWeek ?? null,
        period: occurrence.period ?? null,
        roomId: occurrence.roomId ?? null
      }))
    )
  }

  function splitTeacherSubjects(subject: string): string[] {
    return subject
      .split(/[、,，/]/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  // ==================== 转换函数 ====================

  /** 课时的全部任教教师 ID（多师同堂）；无列表时回退到单教师字段 */
  function occurrenceTeacherIds(occurrence: ScheduleOccurrenceDraft): number[] {
    if (Array.isArray(occurrence.teacherIds) && occurrence.teacherIds.length > 0) {
      return occurrence.teacherIds
    }
    return occurrence.teacherId != null ? [occurrence.teacherId] : []
  }

  /**
   * 合班课时的合并任教教师（自身 + 同课位同课程的合班伙伴，按教师去重）。
   * 返回 null 表示非合班课时或无伙伴，调用方沿用自身教师展示。
   *
   * 注意：联动闭包同时包含「同时开课」「单双周」伙伴，它们只是时间对齐，
   * 各班教师相互独立，必须用合班规则过滤，不能并入展示。
   */
  function mergedCombinedTeachers(
    occurrence: ScheduleOccurrenceDraft
  ): { ids: number[]; names: string[] } | null {
    const ruleSettings = useScheduleStore().currentTask?.rulesConfig?.ruleSettings
    const partners = conflictsComposable
      .resolveLinkedOccurrences(occurrence.key)
      .filter(
        (partner) =>
          partner.key !== occurrence.key &&
          partner.courseId != null &&
          partner.courseId === occurrence.courseId &&
          partner.placed &&
          partner.dayOfWeek != null &&
          partner.period != null &&
          partner.dayOfWeek === occurrence.dayOfWeek &&
          partner.period === occurrence.period &&
          conflictsComposable.isCombinedClassPair(occurrence, partner, ruleSettings)
      )
    if (partners.length === 0) {
      return null
    }
    const seen = new Set<string>()
    const ids: number[] = []
    const names: string[] = []
    for (const member of [occurrence, ...partners]) {
      const memberIds = occurrenceTeacherIds(member)
      const memberNames = Array.isArray(member.teacherNames) && member.teacherNames.length > 0
        ? member.teacherNames
        : member.teacherName ? [member.teacherName] : []
      memberIds.forEach((id, index) => {
        const name = memberNames[index] ?? ''
        if (seen.has(`id:${id}`)) return
        seen.add(`id:${id}`)
        ids.push(id)
        if (name) names.push(name)
      })
      // 仅有姓名无 ID 的教师按姓名去重补入
      memberNames.slice(memberIds.length).forEach((name) => {
        if (!name || seen.has(`name:${name}`)) return
        seen.add(`name:${name}`)
        names.push(name)
      })
    }
    return { ids, names }
  }

  function draftToSlot(occurrence: ScheduleOccurrenceDraft): ScheduleSlot {
    const teacherIds = occurrenceTeacherIds(occurrence)
    const teacherNames = Array.isArray(occurrence.teacherNames) && occurrence.teacherNames.length > 0
      ? occurrence.teacherNames
      : occurrence.teacherName ? [occurrence.teacherName] : []
    const teacherName = teacherNames.length > 0 ? teacherNames.join('、') : occurrence.teacherName
    return {
      day: occurrence.dayOfWeek ?? 0,
      period: occurrence.period ?? 0,
      classId: occurrence.classId ?? null,
      className: occurrence.className,
      subgroupId: occurrence.subgroupId ?? null,
      subgroupName: occurrence.subgroupName ?? null,
      courseId: occurrence.courseId ?? null,
      courseName: occurrence.courseName,
      course: occurrence.courseName,
      teacherId: occurrence.teacherId ?? (teacherIds.length > 0 ? teacherIds[0] : null),
      teacherName,
      teacher: teacherName,
      teacherIds,
      teacherNames,
      roomId: occurrence.roomId ?? null,
      roomName: occurrence.roomName ?? null,
      duration: occurrence.duration,
      taskActivityId: occurrence.taskActivityId,
      occurrenceIndex: occurrence.occurrenceIndex,
      occurrenceKey: occurrence.key,
      placed: occurrence.placed
    }
  }

  function buildStagedCourses(occurrences: ScheduleOccurrenceDraft[]): StagedCourse[] {
    return sortDraftOccurrences(occurrences)
      .filter((occurrence) => !occurrence.placed)
      .map((occurrence) => ({
        id: occurrence.key,
        occurrenceKey: occurrence.key,
        taskActivityId: occurrence.taskActivityId,
        occurrenceIndex: occurrence.occurrenceIndex,
        courseId: occurrence.courseId ?? null,
        courseName: occurrence.courseName,
        teacherId: occurrence.teacherId ?? null,
        teacherName: occurrence.teacherName,
        classId: occurrence.classId ?? null,
        className: occurrence.className,
        subgroupId: occurrence.subgroupId ?? null,
        subgroupName: occurrence.subgroupName ?? null,
        remainingHours: 1,
        totalHours: occurrence.duration,
        originalDay: occurrence.dayOfWeek ?? null,
        originalPeriod: occurrence.period ?? null
      }))
  }

  function buildClassSchedule(
    occurrences: ScheduleOccurrenceDraft[],
    classId: number,
    subgroupId?: number | null
  ): ScheduleSlot[] {
    const targetSubgroupId = subgroupId ?? null
    return occurrences
      .filter(
        (occurrence) =>
          occurrence.placed &&
          occurrence.classId === classId &&
          isSubgroupVisibleForTarget(occurrence.subgroupId, targetSubgroupId) &&
          occurrence.dayOfWeek != null &&
          occurrence.period != null
      )
      .map((occurrence) => {
        const slot = draftToSlot(occurrence)
        // 合班上课：各班教师在格子内合并去重展示（与多师同堂同一展示方式）
        const merged = mergedCombinedTeachers(occurrence)
        if (merged && merged.names.length > 0) {
          const joined = merged.names.join('、')
          slot.teacherIds = merged.ids
          slot.teacherNames = merged.names
          slot.teacherName = joined
          slot.teacher = joined
          slot.teacherId = merged.ids.length > 0 ? merged.ids[0] : slot.teacherId
        }
        return slot
      })
      .sort((left, right) => left.day - right.day || left.period - right.period)
  }

  function buildTeacherScheduleFromDrafts(
    occurrences: ScheduleOccurrenceDraft[],
    teacher: ScheduleTeacherOption | null
  ): TeacherSchedule | null {
    if (!teacher) {
      return null
    }

    return {
      teacherId: teacher.id,
      teacherName: teacher.name,
      name: teacher.name,
      subject: teacher.subject,
      subjectNames: splitTeacherSubjects(teacher.subject),
      schedule: occurrences
        .filter(
          (occurrence) =>
            occurrence.placed &&
            occurrenceTeacherIds(occurrence).includes(teacher.id) &&
            occurrence.dayOfWeek != null &&
            occurrence.period != null
        )
        .map((occurrence) => draftToSlot(occurrence))
        .sort((left, right) => left.day - right.day || left.period - right.period)
    }
  }

  function mergeTeacherOptions(
    task: ScheduleTaskDetail | null,
    snapshot: ScheduleSnapshotDetail | null,
    draftOccurrences: ScheduleOccurrenceDraft[]
  ): ScheduleTeacherOption[] {
    const optionMap = new Map<number, ScheduleTeacherOption>()

    for (const option of task ? buildScheduleTeacherOptions(task) : []) {
      optionMap.set(option.id, option)
    }

    for (const teacher of snapshot?.teacherPreviews ?? []) {
      if (teacher.teacherId == null) {
        continue
      }
      optionMap.set(teacher.teacherId, {
        id: teacher.teacherId,
        name: teacher.teacherName,
        subject: teacher.subject
      })
    }

    for (const occurrence of draftOccurrences) {
      const ids = occurrenceTeacherIds(occurrence)
      const names = Array.isArray(occurrence.teacherNames) ? occurrence.teacherNames : []
      ids.forEach((teacherId, index) => {
        const teacherName = names[index] ?? (index === 0 ? occurrence.teacherName : '')
        if (!teacherName || optionMap.has(teacherId)) {
          return
        }
        optionMap.set(teacherId, {
          id: teacherId,
          name: teacherName,
          subject: ''
        })
      })
    }

    return sortTeacherOptionsBySubjectAndName(
      [...optionMap.values()],
      task ? buildSubjectWeeklyFrequencyMap(task) : new Map<string, number>()
    )
  }

  function overlayDraftOccurrencesFromResults(
    baseOccurrences: ScheduleOccurrenceDraft[],
    schedulesByClass: Record<number, ScheduleSlot[]>
  ): ScheduleOccurrenceDraft[] {
    const draftMap = new Map(
      baseOccurrences.map((occurrence) => [occurrence.key, cloneDraftOccurrence(occurrence)])
    )

    for (const schedule of Object.values(schedulesByClass)) {
      for (const slot of schedule) {
        if (!slot.taskActivityId || !slot.occurrenceIndex) {
          continue
        }

        const occurrence = draftMap.get(createOccurrenceKey(slot.taskActivityId, slot.occurrenceIndex))
        if (!occurrence) {
          continue
        }

        occurrence.placed = slot.placed ?? true
        occurrence.dayOfWeek = slot.day
        occurrence.period = slot.period
        occurrence.roomId = slot.roomId ?? null
        occurrence.roomName = slot.roomName ?? null

        if (slot.teacherId != null) {
          occurrence.teacherId = slot.teacherId
        }
        if (slot.teacherName) {
          occurrence.teacherName = slot.teacherName
        }
        if (Array.isArray(slot.teacherIds) && slot.teacherIds.length > 0) {
          occurrence.teacherIds = slot.teacherIds
        }
        if (Array.isArray(slot.teacherNames) && slot.teacherNames.length > 0) {
          occurrence.teacherNames = slot.teacherNames
        }
      }
    }

    return sortDraftOccurrences([...draftMap.values()])
  }

  // ==================== 操作函数 ====================

  function findOccurrence(key: string): ScheduleOccurrenceDraft | null {
    const store = useScheduleStore()
    return store.draftOccurrences.find((occurrence: ScheduleOccurrenceDraft) => occurrence.key === key) ?? null
  }

  function moveOccurrenceToSlot(key: string, dayOfWeek: number, period: number, force = false): ConflictInfo {
    const store = useScheduleStore()
    const occurrence = findOccurrence(key)
    if (!occurrence) {
      return {
        hasConflict: false,
        conflictType: 'none' as const,
        message: '未找到需要移动的课时。'
      }
    }

    // 对调方案（与预检 checkDragConflict 共用，保证两者判定一致）：
    // - moverGroup：移动组（含联动闭包）→ 目标位
    // - returningGroup：目标格占位课及其联动闭包 → 源位
    // - displacedGroup：回源成员班级源格上的被顶替课 → 目标位
    const plan = conflictsComposable.buildLinkedSwapPlan(key, dayOfWeek, period)
    if (!plan) {
      return {
        hasConflict: false,
        conflictType: 'none' as const,
        message: '未找到需要移动的课时。'
      }
    }
    const { moverGroup, returningGroup, displacedGroup, reject } = plan

    // 硬性拦截：双边均带联动（C 场景）或被顶替课自身带联动。
    // 属于结构性不可操作，force 也不能绕过；不落任何改动。
    if (reject) {
      const message = conflictsComposable.linkedSwapRejectMessage(reject)
      logger.warn('对调被联动关系拒绝', {
        key,
        dayOfWeek,
        period,
        kind: reject.kind,
        occupant: reject.occupant.key,
        partner: reject.partner.key
      })
      return {
        hasConflict: true,
        conflictType: 'linked' as const,
        message
      }
    }

    const moverKeys = new Set(moverGroup.map((member) => member.key))
    const returningKeys = new Set(returningGroup.map((member) => member.key))
    const displacedKeys = new Set(displacedGroup.map((member) => member.key))
    const sourceDay = occurrence.dayOfWeek ?? null
    const sourcePeriod = occurrence.period ?? null
    const fromStaging = !occurrence.placed || sourceDay == null || sourcePeriod == null

    // 逐门校验（与预检同范围，未 force 时）：
    // - 目标侧（移动组 + 被顶替课）查目标位，排除移动组与回源组（回源组会让出目标格）
    // - 回源组查源位，排除移动组与被顶替课（它们会让出源格）
    if (!force) {
      const targetExcludeKeys = new Set([...moverKeys, ...returningKeys])
      for (const member of [...moverGroup, ...displacedGroup]) {
        const conflict = conflictsComposable.buildConflictInfo(
          store.draftOccurrences, member, dayOfWeek, period, targetExcludeKeys)
        if (conflict.hasConflict) {
          logger.warn('拖动冲突并拦截', { key, dayOfWeek, period, conflict })
          return conflict
        }
      }
      if (!fromStaging && sourceDay != null && sourcePeriod != null) {
        const sourceExcludeKeys = new Set([...moverKeys, ...displacedKeys])
        for (const occupant of returningGroup) {
          const conflict = conflictsComposable.buildConflictInfo(
            store.draftOccurrences, occupant, sourceDay, sourcePeriod, sourceExcludeKeys)
          if (conflict.hasConflict) {
            logger.warn('对调回源冲突并拦截', { key, dayOfWeek, period, conflict })
            return conflict
          }
        }
      }
    }

    // 落定语义与新版排课详情共用同一实现，避免两处对调逻辑漂移
    applyLinkedSwapPlan(plan, { dayOfWeek, period }, { sourceDay, sourcePeriod, fromStaging })

    if (returningGroup.length > 0) {
      logger.debug('联动互换课位', {
        moverKeys: [...moverKeys],
        to: { dayOfWeek, period },
        returningKeys: [...returningKeys],
        displacedKeys: [...displacedKeys]
      })
    } else {
      logger.debug('联动移动课位（强制或无冲突）', {
        moverKeys: [...moverKeys],
        to: { dayOfWeek, period }
      })
    }

    return {
      hasConflict: false,
      conflictType: 'none' as const,
      message: ''
    }
  }

  function moveOccurrenceToStaging(key: string): boolean {
    const occurrence = findOccurrence(key)
    if (!occurrence) {
      return false
    }

    // 联动组一起移入暂存区，避免合班/同时开课/单双周关系被拆散
    const group = conflictsComposable.resolveLinkedOccurrences(key)
    for (const member of group) {
      member.placed = false
      member.dayOfWeek = null
      member.period = null
      member.roomId = null
      member.roomName = null
    }
    logger.debug('移入暂存区', { key, linkedKeys: group.map((member) => member.key) })
    return true
  }

  function discardChanges(): void {
    const store = useScheduleStore()
    store.draftOccurrences = cloneDraftOccurrences(store.baselineDraftOccurrences)
  }

  function applyDraftOccurrences(
    occurrences: ScheduleOccurrenceDraft[],
    snapshot: ScheduleSnapshotDetail | null
  ): void {
    const store = useScheduleStore()
    store.draftOccurrences = cloneDraftOccurrences(occurrences)
    store.baselineDraftOccurrences = cloneDraftOccurrences(occurrences)
    store.activeSnapshot = snapshot
  }

  function markBaselineFromSave(
    occurrences: ScheduleOccurrenceDraft[],
    snapshot: ScheduleSnapshotDetail | null
  ): void {
    const store = useScheduleStore()
    const changedCount = store.draftOccurrences.filter((d) => {
      const base = occurrences.find((o) => o.key === d.key)
      return base && (base.dayOfWeek !== d.dayOfWeek || base.period !== d.period || base.placed !== d.placed)
    }).length
    logger.debug('[ScheduleDraft] 保存后更新基线:', {
      snapshotId: snapshot?.id,
      baselineOccurrenceCount: occurrences.length,
      draftDivergence: changedCount,
      note: changedCount > 0 ? '用户在保存期间做了额外修改，draftOccurrences 已保留' : 'draftOccurrences 与基线一致'
    })
    store.baselineDraftOccurrences = cloneDraftOccurrences(occurrences)
    store.activeSnapshot = snapshot
  }

  async function hydrateDraftFromResults(task: ScheduleTaskDetail): Promise<void> {
    const baseOccurrences = buildDraftOccurrencesFromTask(task)
    const schedulesByClass: Record<number, ScheduleSlot[]> = {}

    const classIds = [...new Set(buildScheduleClassOptions(task).map((option: ScheduleClassOption) => option.id))]
    await Promise.all(
      classIds.map(async (classId) => {
        schedulesByClass[classId] = await scheduleApi.getClassSchedule(task.id, classId)
      })
    )

    applyDraftOccurrences(overlayDraftOccurrencesFromResults(baseOccurrences, schedulesByClass), null)
  }

  async function loadActiveSnapshot(snapshotId: number): Promise<void> {
    const store = useScheduleStore()
    if (!store.currentTask) {
      return
    }

    const snapshot = await scheduleApi.getSnapshot(store.currentTask.id, snapshotId)
    applyDraftOccurrences(buildDraftOccurrencesFromSnapshot(snapshot), snapshot)
    store.ensureSelections()
  }

  return {
    // 转换函数
    buildStagedCourses,
    buildClassSchedule,
    buildTeacherScheduleFromDrafts,
    mergeTeacherOptions,
    overlayDraftOccurrencesFromResults,

    // 操作函数
    findOccurrence,
    moveOccurrenceToSlot,
    moveOccurrenceToStaging,
    discardChanges,
    applyDraftOccurrences,
    markBaselineFromSave,
    serializeDraftOccurrences,
    hydrateDraftFromResults,
    loadActiveSnapshot
  }
}
